// MainLayout.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Layout } from "antd";
import { Header as MainHeader } from "../../components/Header/Header";
import { SlideContents } from "../../components/Slide/SlideContents/SlideContents";
import Slides from "../../components/Slide/Slides";
import { SpeakerNotes } from "../../components/Slide/SpeakerNotes/SpeakerNotes";
import { Toolbar } from "../../components/Toolbar/Toolbar";
import ContextMenu from "../../ContextMenu/ContextMenu";
import { ChatBox } from "../../components/ChatBox/ChatBox";
import OfflineStatus from "../../components/OfflineStatus/OfflineStatus";
import MouseContainer from "../mousecontainer";
import MasterSlide from "../MasterSlide";
import Loader from "../SkeletonLoader/Loader";
import { useDispatch, useSelector } from "react-redux";
import {
    emitRoomID,
    listenElementAdd,
    listenElementDelete,
    listenShapeTextUpdate,
    listenWordArtTextUpdate,
    listenSmartArtNodesUpdate,
    listenSlideAdd,
    listenSlideColor,
    listenSlideDelete,
    listenSpeakerNotes,
    listenStyleUpdate,
    listenTextUpdate
} from "../../Socket/Socket";
import { slideAPI } from "../../store/api";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { slideAction } from "../../store/slideSlice";
import PropertiesDrawer from "../PropertiesModal";
import useContextMenu from "../../hooks/use-contextMenu";
import { useDocumentCache } from "../../hooks/useDocumentCache";
import { hasCachedDocument } from "../../utils/documentCache";
import {
    getLocalPresentation,
    updateLocalPresentationContent,
    isLocalPresentationRoute,
} from "../../utils/localPresentations";
import { AnimationsPane } from "../Animations";
import SyncPresentationModal from "../SyncPresentationModal/SyncPresentationModal";
import { useReconnectSyncPrompt } from "../SyncPresentationModal/useReconnectSyncPrompt";
const { Sider, Content } = Layout;

const MainLayout = () => {
    const { id } = useParams('id');
    const dispatch = useDispatch();
    const { createPresentationById } = slideAPI;
    const [isContentLoading, setIsContentLoading] = useState(true);
    const isLocalPresentation = useMemo(() => isLocalPresentationRoute(), []);
    const isMasterSlide = useSelector((state) => state.slide.isMasterSlide);
    const isReadOnly = useSelector((state) => state.slide.isReadOnly);
    const isTemplateDocument = useSelector((state) => state.slide.isTemplateDocument);
    const slides = useSelector((state) => state.slide.slides);
    const currentSlideId = useSelector((state) => state.slide.currentSlideId);
    const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);
    const documentInformation = useSelector((state) => state.slide.documentInformation);
    const [showPropertiesModal, setShowPropertiesModal] = useState(false);
    const { elemRef } = useContextMenu();

    // Count of elements on the current slide; changes only on insert/delete.
    const currentSlideElementCount = useMemo(() => {
        const slide = slides?.[currentSlideId];
        if (!slide) return 0;
        return (
            Object.keys(slide.shapes || {}).length +
            Object.keys(slide.image || {}).length +
            Object.keys(slide.smartArtObjects || {}).length +
            Object.keys(slide.wordArtTextObjects || {}).length +
            Object.keys(slide.textBox || {}).length
        );
    }, [slides, currentSlideId]);

    // Close the properties drawer on insert so it doesn't linger over the
    // previously selected element. Slide switches just reset the baseline.
    const prevElementCountRef = useRef(currentSlideElementCount);
    const prevSlideIdRef = useRef(currentSlideId);
    useEffect(() => {
        if (prevSlideIdRef.current !== currentSlideId) {
            prevSlideIdRef.current = currentSlideId;
            prevElementCountRef.current = currentSlideElementCount;
            return;
        }
        if (currentSlideElementCount > prevElementCountRef.current && showPropertiesModal) {
            setShowPropertiesModal(false);
        }
        prevElementCountRef.current = currentSlideElementCount;
    }, [currentSlideElementCount, currentSlideId, showPropertiesModal]);

    // Both are right-hand drawers at the same position, so they would stack. Enforced here
    // rather than at each opener, so the toolbar button and the context menu both get it.
    const animationPaneOpen = useSelector((state) => state.slide.animationPaneOpen);
    useEffect(() => {
        if (animationPaneOpen && showPropertiesModal) setShowPropertiesModal(false);
    }, [animationPaneOpen, showPropertiesModal]);

    // Caching is limited to normal, editable, server-backed documents.
    const cacheEnabled = !isTemplateDocument && !isReadOnly && !isLocalPresentation;
    const { cached, isUiLocked, lockUi, unlockUi, cacheRenderedRef, commitCache } =
        useDocumentCache(id, cacheEnabled);

    // Non-mutating probe: true when a renderable cache entry exists for this doc.
    const isDocumentCached = useMemo(
        () => cacheEnabled && hasCachedDocument(id),
        [cacheEnabled, id]
    );

    // Show the Header + offline status the instant a cache hit can paint, and
    // keep them mounted even as the background fetch toggles isContentLoading.
    const showChrome = !isContentLoading || isDocumentCached;

    // Handle mouse hover for cursor change
    const handleMousHover = (e) => {
        if (
            e.target.classList.contains("ql-container") ||
            e.target.classList.contains("shape") ||
            e.target.classList.contains("wordart")
        ) {
            e.target.style.cursor = "move";
        }
    };


    // Instant paint: seed the editor with cached content before the browser
    // paints, so a cache hit shows real slides with no loader flash. We do NOT
    // flip "ready" (isContentLoading stays true) — the background fetch still
    // swaps in fresh data through the normal init path below.
    useLayoutEffect(() => {
        if (
            cacheEnabled &&
            cached?.documentData &&
            !cacheRenderedRef.current
        ) {
            dispatch(slideAction.loadPresentation(cached.documentData));
            cacheRenderedRef.current = true;
            lockUi();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!id) return;

        dispatch(slideAction.setPresentationId(id));

        // Local presentations load from localStorage and never join a room.
        if (isLocalPresentation) {
            const record = getLocalPresentation(id);
            if (record?.content) {
                dispatch(slideAction.loadPresentation(record.content));
            }
            setIsContentLoading(false);
            return;
        }

        createPresentationById(id, dispatch, setIsContentLoading);
        emitRoomID(id);

        // Set up socket listeners
        listenSlideAdd(dispatch);
        listenElementAdd(dispatch);
        listenSlideDelete(dispatch);
        listenElementDelete(dispatch);
        listenStyleUpdate(dispatch);
        listenSlideColor(dispatch);
        listenSpeakerNotes(dispatch);
        listenTextUpdate(dispatch);
        listenShapeTextUpdate(dispatch);
        listenWordArtTextUpdate(dispatch);
        listenSmartArtNodesUpdate(dispatch);
    }, [id, isLocalPresentation]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Tab") {
                if (isReadOnly) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isReadOnly]);

    // When the background fetch resolves it flips "ready" (isContentLoading
    // false) and replaces the cached preview with fresh data — release the lock.
    useEffect(() => {
        if (!isContentLoading && isUiLocked) {
            unlockUi();
        }
    }, [isContentLoading, isUiLocked, unlockUi]);

    // Write-back: mirror the latest user-visible state into the cache, driven by
    // the in-memory content so unsaved edits win over stale API data. Skipped
    // while the locked preview is on screen (never persist the preview itself).
    useEffect(() => {
        if (!cacheEnabled || isUiLocked) return;
        // Never persist the cached preview itself: a preview is on screen while
        // it has been painted but fresh data has not yet arrived (still loading).
        if (cacheRenderedRef.current && isContentLoading) return;
        if (!slides || Object.keys(slides).length === 0) return;
        const documentData = { currentSlideId, slides, mirrorSlides };
        commitCache(documentData, documentInformation);
    }, [cacheEnabled, isUiLocked, isContentLoading, slides, currentSlideId, mirrorSlides, documentInformation, commitCache, cacheRenderedRef]);

    // Local presentations persist to localStorage only — there is no API behind
    // them. Debounced so a burst of edits doesn't serialise the whole deck on
    // every keystroke.
    const localContentRef = useRef(null);
    useEffect(() => {
        if (!isLocalPresentation || !id || isContentLoading) return;

        localContentRef.current = { slides, mirrorSlides, currentSlideId };
        const timer = setTimeout(() => {
            updateLocalPresentationContent(id, localContentRef.current);
        }, 500);

        return () => clearTimeout(timer);
    }, [isLocalPresentation, id, isContentLoading, slides, mirrorSlides, currentSlideId]);

    // Force the pending debounced write out. Shared by the unload handlers and
    // by the sync prompt, which must not upload a stale deck.
    const flushLocalContent = useCallback(() => {
        if (!isLocalPresentation || !id) return;
        if (localContentRef.current) {
            updateLocalPresentationContent(id, localContentRef.current);
        }
    }, [isLocalPresentation, id]);

    // Flush on hide/unload so a pending debounce is never lost with the tab.
    useEffect(() => {
        if (!isLocalPresentation || !id) return;

        window.addEventListener('pagehide', flushLocalContent);
        document.addEventListener('visibilitychange', flushLocalContent);
        return () => {
            window.removeEventListener('pagehide', flushLocalContent);
            document.removeEventListener('visibilitychange', flushLocalContent);
            flushLocalContent();
        };
    }, [isLocalPresentation, id, flushLocalContent]);

    // Offer to sync this deck to the server whenever syncing is possible.
    const { syncDoc, closeSyncModal, handleSynced } = useReconnectSyncPrompt({
        id,
        isLocalPresentation,
        isContentLoading,
        flushLocalContent,
    });

    return (
        <div className="App">
            {isContentLoading && !isDocumentCached && <Loader />}
            {showChrome && (
                <div className="main-layout-content-fade-in">
                    <MainHeader />
                    <SyncPresentationModal
                        open={!!syncDoc}
                        doc={syncDoc}
                        onClose={closeSyncModal}
                        onSynced={handleSynced}
                    />
                    <div id='offlineStatusBar'>
                        <OfflineStatus />
                    </div>
                    <Toolbar />
                    {/* Overlays the right edge rather than docking, so opening it never
                        shifts the slide. Manages its own visibility from the store. */}
                    <AnimationsPane currentSlideId={currentSlideId} />
                    <Layout id='slide-layout'>
                        <Layout>
                            <Sider className="sidebar-container" width={window.screen.width < 992 ? '100%' : '15.5%'} id='my-sideBar'>
                                <Slides />
                            </Sider>
                            <Content id="mainContainer" className='main-container' style={{ backgroundColor: '#f0f2f5', display: 'flex', justifyContent: 'center', paddingTop: '24px', minHeight: 'calc(100vh - 95px)', overflowY: 'auto' }} onMouseOver={handleMousHover}>
                                <div id='delete-menu'>
                                    <ContextMenu
                                        showPropertiesModal={showPropertiesModal}
                                        setShowPropertiesModal={setShowPropertiesModal}
                                    />
                                    {
                                        showPropertiesModal &&
                                        <PropertiesDrawer
                                            isModalVisible={showPropertiesModal}
                                            setShowPropertiesModal={setShowPropertiesModal}
                                            elemRef={elemRef}
                                        />
                                    }
                                </div>
                                {isMasterSlide && (
                                    <div id='masterSlide-Conatiner'>
                                        <MasterSlide />
                                    </div>
                                )}
                                <SlideContents />
                                <div id='speakerNotes'>
                                    {window.screen.width > 992 && <SpeakerNotes />}
                                </div>
                            </Content>
                        </Layout>
                    </Layout>
                    <div id='chatbox-container'>
                        <ChatBox />
                    </div>
                    <div id='mouseContainer'>
                        <MouseContainer />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MainLayout;